// src/services/prepaPhysiqueServices.js

import axios from "axios";

const apiUrl = `${process.env.REACT_APP_API_URL}/api/prepa`;

function getAuthHeaders(tokenParam) {
  const authToken = tokenParam || localStorage.getItem("token");

  return {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  };
}

function normalizeNullableString(value) {
  const cleanValue = String(value || "").trim();
  return cleanValue || null;
}

function normalizeNumberOrNull(value) {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  const numberValue = Number(value);

  return Number.isNaN(numberValue) ? null : numberValue;
}

function normalizeBoolean(value) {
  return value === true || value === "true" || value === 1 || value === "1";
}

export function normalizePrepaExercisePayload(form = {}) {
  return {
    nom: String(form.nom || "").trim(),
    description: normalizeNullableString(form.description),
    lien_photo: normalizeNullableString(form.lien_photo),
    lien_video: normalizeNullableString(form.lien_video),
    programme: normalizeNullableString(form.programme),
  };
}

export function normalizeAdherentPrepaResultPayload(form = {}) {
  return {
    adherent_id: Number(form.adherent_id),
    prepa_physique_id: Number(form.prepa_physique_id),
    date_realisation: form.date_realisation || null,
    repetitions: normalizeNumberOrNull(form.repetitions),
    is_done: normalizeBoolean(form.is_done),
  };
}

/**
 * GET
 * Récupérer tous les exercices de préparation physique disponibles.
 */
export const getAllPrepaExercises = async (tokenParam) => {
  try {
    const response = await axios.get(`${apiUrl}/all`, getAuthHeaders(tokenParam));
    return response.data;
  } catch (error) {
    console.error(
      "Erreur lors de la récupération des exercices de préparation physique :",
      error,
    );
    throw error;
  }
};

/**
 * GET
 * Récupérer tous les exercices réalisés par un adhérent.
 */
export const getPrepaExercisesByAdherent = async (adherentId, tokenParam) => {
  try {
    const response = await axios.get(
      `${apiUrl}/adherent/${adherentId}`,
      getAuthHeaders(tokenParam),
    );

    return response.data;
  } catch (error) {
    console.error(
      `Erreur lors de la récupération des exercices de l'adhérent ${adherentId} :`,
      error,
    );
    throw error;
  }
};

/**
 * GET
 * Récupérer tous les exercices réalisés par tous les adhérents.
 */
export const getAllAdherentPrepaExercises = async (tokenParam) => {
  try {
    const response = await axios.get(
      `${apiUrl}/all-adherents`,
      getAuthHeaders(tokenParam),
    );

    return response.data;
  } catch (error) {
    console.error(
      "Erreur lors de la récupération des exercices réalisés par les adhérents :",
      error,
    );
    throw error;
  }
};

/**
 * GET
 * Récupérer les détails / historiques d'un exercice spécifique pour un adhérent.
 */
export const getAdherentExerciseDetails = async (
  adherentId,
  exerciseId,
  tokenParam,
) => {
  try {
    const response = await axios.get(
      `${apiUrl}/adherent/${adherentId}/exercise/${exerciseId}`,
      getAuthHeaders(tokenParam),
    );

    return response.data;
  } catch (error) {
    console.error(
      `Erreur lors de la récupération du détail de l'exercice ${exerciseId} pour l'adhérent ${adherentId} :`,
      error,
    );
    throw error;
  }
};

/**
 * POST
 * Créer un nouvel exercice de préparation physique.
 */
export const createPrepaExercise = async (form, tokenParam) => {
  try {
    const payload = normalizePrepaExercisePayload(form);

    const response = await axios.post(
      `${apiUrl}/create`,
      payload,
      getAuthHeaders(tokenParam),
    );

    return response.data;
  } catch (error) {
    console.error(
      "Erreur lors de la création de l'exercice de préparation physique :",
      error,
    );
    throw error;
  }
};

/**
 * POST
 * Ajouter / enregistrer une réalisation d'exercice pour un adhérent.
 */
export const addAdherentExerciseResult = async (form, tokenParam) => {
  try {
    const payload = normalizeAdherentPrepaResultPayload(form);

    const response = await axios.post(
      `${apiUrl}/adherent/add-result`,
      payload,
      getAuthHeaders(tokenParam),
    );

    return response.data;
  } catch (error) {
    console.error(
      "Erreur lors de l'enregistrement du résultat de préparation physique :",
      error,
    );
    throw error;
  }
};

/**
 * PUT
 * Modifier un exercice de préparation physique.
 */
export const editPrepaExercise = async (exerciseId, form, tokenParam) => {
  try {
    const payload = normalizePrepaExercisePayload(form);

    const response = await axios.put(
      `${apiUrl}/edit/${exerciseId}`,
      payload,
      getAuthHeaders(tokenParam),
    );

    return response.data;
  } catch (error) {
    console.error(
      `Erreur lors de la modification de l'exercice ${exerciseId} :`,
      error,
    );
    throw error;
  }
};

/**
 * PUT
 * Modifier une réalisation d'exercice pour un adhérent.
 *
 * Avec tes routes actuelles, le back attend :
 * {
 *   adherent_id,
 *   prepa_physique_id,
 *   date_realisation,
 *   repetitions,
 *   is_done
 * }
 */
export const updateAdherentExerciseResult = async (form, tokenParam) => {
  try {
    const payload = normalizeAdherentPrepaResultPayload(form);

    const response = await axios.put(
      `${apiUrl}/adherent/update-result`,
      payload,
      getAuthHeaders(tokenParam),
    );

    return response.data;
  } catch (error) {
    console.error(
      "Erreur lors de la modification du résultat de préparation physique :",
      error,
    );
    throw error;
  }
};

/**
 * DELETE
 * Supprimer un exercice de préparation physique du catalogue.
 */
export const deletePrepaExercise = async (exerciseId, tokenParam) => {
  try {
    const response = await axios.delete(
      `${apiUrl}/delete/${exerciseId}`,
      getAuthHeaders(tokenParam),
    );

    return response.data;
  } catch (error) {
    console.error(
      `Erreur lors de la suppression de l'exercice ${exerciseId} :`,
      error,
    );
    throw error;
  }
};

/**
 * DELETE
 * Supprimer l'enregistrement d'un adhérent sur un exercice.
 *
 * Route actuelle :
 * /api/prepa/adherent/delete-result/:adherentId/:exerciseId
 */
export const deleteAdherentExerciseResult = async (
  adherentId,
  exerciseId,
  tokenParam,
) => {
  try {
    const response = await axios.delete(
      `${apiUrl}/adherent/delete-result/${adherentId}/${exerciseId}`,
      getAuthHeaders(tokenParam),
    );

    return response.data;
  } catch (error) {
    console.error(
      `Erreur lors de la suppression du résultat de l'exercice ${exerciseId} pour l'adhérent ${adherentId} :`,
      error,
    );
    throw error;
  }
};